import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CobaltTriangle, OrangeArc, StripedTriangle } from "../components/Motifs";
import { IMG, LOGO } from "../lib/site";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-8 pb-14 lg:pt-10 lg:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6 relative z-10">
            <Reveal>
              <div className="font-display leading-tight">
                <span className="text-3xl lg:text-4xl text-ink">Elenem </span>
                <span className="text-3xl lg:text-4xl italic text-cobalt">Strategies</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-tight text-ink">
                Where <em className="text-cobalt italic">exploration</em> becomes{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">innovation.</span>
                  <span className="absolute -bottom-1.5 left-0 right-0 h-2.5 bg-orange-brand/70 -skew-x-6 z-0" />
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base lg:text-lg leading-relaxed text-ink-soft max-w-xl">
                The strongest ideas don't emerge from standing still. They come from exploring new perspectives,
                engaging the right people, and having the courage to build something better.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link
                  to="/collaborate"
                  data-testid="hero-cta-collaborate"
                  className="group inline-flex items-center gap-2 bg-cobalt text-cream px-7 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-cobalt-deep transition-colors"
                >
                  Collaborate
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 border border-ink text-ink px-7 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-ink hover:text-cream transition-colors"
                >
                  Explore our services
                </Link>
                <Link
                  to="/meet"
                  data-testid="hero-cta-meet"
                  className="group inline-flex items-center gap-2 bg-orange-brand text-cream px-7 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-orange-soft transition-colors"
                >
                  Meet Leah
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Right: hero image with motifs */}
          <div className="lg:col-span-6 relative">
            <Reveal delay={0.15}>
              <div className="relative aspect-[5/4] lg:aspect-[5/4] w-full max-h-[520px] overflow-hidden">
                <img
                  src={IMG.heroMountain}
                  alt="Leah on a snowy mountaintop pointing forward"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                  fetchpriority="high"
                  decoding="async"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
              </div>
            </Reveal>
            <CobaltTriangle
              className="absolute -top-5 -right-5 z-10 hidden md:block animate-slow-drift"
              size={110}
              flip
            />
            <OrangeArc className="absolute -bottom-6 -left-6 z-10 hidden md:block" size={100} />
            <StripedTriangle className="absolute -bottom-8 -right-8 -z-0 hidden md:block" size={140} />
          </div>
        </div>
      </section>

      {/* ---------- MANIFESTO ---------- */}
      <section className="relative py-24 lg:py-36 bg-cream-alt overflow-hidden">
        <StripedTriangle className="absolute top-0 left-0 opacity-70" size={340} />
        <OrangeArc className="absolute -bottom-10 -right-10" size={220} />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="overline">Our conviction</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink max-w-4xl">
              At Elenem Strategies, <em className="italic text-cobalt">we believe—</em>
            </h2>
          </Reveal>

          {/* Text with 3 scattered images */}
          <div className="mt-16 lg:mt-20 relative lg:pb-20">
            {/* Text column, offset left, narrower to leave breathing room */}
            <div className="lg:w-[56%] space-y-10 relative z-10">
              <Reveal delay={0.05}>
                <p className="font-display text-2xl sm:text-3xl lg:text-[2rem] leading-[1.25] text-ink">
                  the challenges facing education and workforce systems are too complex for{" "}
                  <em className="italic text-cobalt">yesterday's solutions</em>.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg lg:text-xl text-ink-soft leading-relaxed">
                  Whether designing career pathways, strengthening talent ecosystems, facilitating statewide
                  partnerships, or helping organizations navigate change, the work begins with{" "}
                  <span className="text-orange-brand font-medium">curiosity</span>.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="font-display text-2xl sm:text-3xl lg:text-[2rem] leading-[1.25] text-ink border-l-2 border-cobalt pl-6">
                  The future isn't waiting for permission from a subcommittee.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-lg lg:text-xl text-ink-soft leading-relaxed">
                  The best ideas rarely come from sitting in the same room with the same people asking the same
                  questions.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="font-display text-2xl sm:text-3xl lg:text-[2rem] leading-[1.25] text-ink">
                  Effective ideas emerge when we invite <em className="italic text-cobalt">new voices</em>, explore{" "}
                  <em className="italic text-orange-brand">new places</em>, test new ideas, and stay willing to
                  learn.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="text-lg lg:text-xl text-ink-soft leading-relaxed">
                  That's what Elenem Strategies is built to do.
                </p>
              </Reveal>
            </div>

            {/* Scattered geometric shapes — muted ambient decoration */}
            <div className="hidden lg:block pointer-events-none opacity-70" aria-hidden="true" data-testid="believe-shapes">
              {/* Shape 1 — striped-triangle badge, upper right */}
              <div className="absolute top-2 right-[12%] w-36 h-36 rotate-[8deg]">
                <div className="w-full h-full stripes-tan border-2 border-cobalt/40" />
              </div>

              {/* Shape 2 — thin cobalt ring outline, middle */}
              <div className="absolute top-[42%] right-[6%] w-32 h-32 -rotate-[6deg]">
                <div className="w-full h-full rounded-full border-2 border-cobalt/40" />
              </div>

              {/* Shape 3 — orange semi-circle with hatching, lower */}
              <div className="absolute top-[78%] right-[22%] w-40 rotate-[4deg]">
                <OrangeArc size={160} />
              </div>

              {/* Small accent dot */}
              <div className="absolute top-[22%] right-[36%] w-3 h-3 rounded-full bg-cobalt/50" />
            </div>

            {/* Mobile: same shapes stacked below the text */}
            <div className="lg:hidden mt-12 grid grid-cols-3 gap-4 items-center opacity-70" aria-hidden="true">
              <div className="aspect-square stripes-tan border border-cobalt/40" />
              <div className="aspect-square rounded-full border-2 border-cobalt/40" />
              <div className="aspect-square flex items-end justify-center">
                <OrangeArc size={120} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- CLOSING BANNER ---------- */}
      <section className="relative py-24 lg:py-32 bg-cobalt text-cream overflow-hidden">
        <StripedTriangle className="absolute -bottom-10 -left-10 opacity-30" size={280} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="font-display italic text-4xl sm:text-5xl lg:text-6xl leading-[1.1]">
              Together we're transforming big ideas into meaningful action.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12">
              <Link
                to="/collaborate"
                data-testid="home-closing-cta"
                className="inline-flex items-center gap-2 bg-orange-brand text-cream px-10 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-orange-soft transition-colors"
              >
                Let's Explore <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
